/**
 * Компонент управления API ключами устройств
 * Дата создания: 29 июля 2025, 20:25 MSK
 */

import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
  LinearProgress,
} from '@mui/material';
import {
  Add as AddIcon,
  Refresh as RefreshIcon,
  Key as KeyIcon,
  ContentCopy as CopyIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Block as RevokeIcon,
  Loop as RotateIcon,
  CheckCircle as ActiveIcon,
  Cancel as InactiveIcon,
  AccessTime as ExpiryIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { deviceApiKeyApi } from '../../services/registrationTokenApi';
import { invalidateDeviceApiKeyData } from '../../utils/cacheInvalidation';
import type {
  DeviceApiKey,
  DeviceApiKeyCreate,
  DeviceApiKeyResponse,
  DeviceApiKeyUpdate,
} from '../../services/registrationTokenApi';

interface Props {
  deviceId: number;
  deviceName: string;
}

export const DeviceApiKeysList: React.FC<Props> = ({ deviceId, deviceName }) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  const [includeInactive, setIncludeInactive] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewKeyDialogOpen, setViewKeyDialogOpen] = useState(false);
  const [selectedKey, setSelectedKey] = useState<DeviceApiKey | null>(null);
  const [newApiKey, setNewApiKey] = useState<string>('');

  // Состояние формы создания ключа
  const [createFormData, setCreateFormData] = useState<DeviceApiKeyCreate>({
    name: '',
    description: '',
    expires_hours: undefined,
  });

  // Состояние формы редактирования ключа
  const [editFormData, setEditFormData] = useState<DeviceApiKeyUpdate>({
    name: '',
    description: '',
    is_active: true,
  });

  // Запрос списка API ключей
  const { data: apiKeys = [], isLoading, error } = useQuery({
    queryKey: ['deviceApiKeys', deviceId, { includeInactive }],
    queryFn: () => deviceApiKeyApi.getDeviceApiKeys(deviceId, {
      include_inactive: includeInactive,
    }),
  });

  // Запрос статистики
  const { data: statistics } = useQuery({
    queryKey: ['deviceApiKeyStats', deviceId],
    queryFn: () => deviceApiKeyApi.getApiKeyStatistics(deviceId),
  });

  // Мутация создания ключа
  const createKeyMutation = useMutation({
    mutationFn: (data: DeviceApiKeyCreate) => deviceApiKeyApi.createApiKey(deviceId, data),
    onSuccess: (response: DeviceApiKeyResponse) => {
      invalidateDeviceApiKeyData(queryClient, deviceId);
      setCreateDialogOpen(false);
      setNewApiKey(response.api_key);
      setViewKeyDialogOpen(true);
      setCreateFormData({
        name: '',
        description: '',
        expires_hours: undefined,
      });
      enqueueSnackbar(t('apiKeys.keyCreatedSuccessfully'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  // Мутация обновления ключа
  const updateKeyMutation = useMutation({
    mutationFn: ({ keyId, data }: { keyId: number; data: DeviceApiKeyUpdate }) =>
      deviceApiKeyApi.updateApiKey(keyId, data),
    onSuccess: () => {
      invalidateDeviceApiKeyData(queryClient, deviceId);
      setEditDialogOpen(false);
      enqueueSnackbar(t('apiKeys.keyUpdatedSuccessfully'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  // Мутация отзыва ключа
  const revokeKeyMutation = useMutation({
    mutationFn: (keyId: number) => deviceApiKeyApi.revokeApiKey(keyId, {
      reason: 'Отозван через веб-интерфейс'
    }),
    onSuccess: () => {
      invalidateDeviceApiKeyData(queryClient, deviceId);
      enqueueSnackbar(t('apiKeys.keyRevokedSuccessfully'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  // Мутация ротации ключей
  const rotateKeysMutation = useMutation({
    mutationFn: () => deviceApiKeyApi.rotateDeviceKeys(deviceId),
    onSuccess: (response: DeviceApiKeyResponse) => {
      invalidateDeviceApiKeyData(queryClient, deviceId);
      setNewApiKey(response.api_key);
      setViewKeyDialogOpen(true);
      enqueueSnackbar(t('apiKeys.keysRotatedSuccessfully'), { variant: 'success' });
    },
    onError: (error: any) => {
      enqueueSnackbar(
        error.response?.data?.detail || t('common.error'),
        { variant: 'error' }
      );
    },
  });

  const handleCreateKey = () => {
    createKeyMutation.mutate(createFormData);
  };

  const handleEditKey = (key: DeviceApiKey) => {
    setSelectedKey(key);
    setEditFormData({
      name: key.name,
      description: key.description || '',
      is_active: key.is_active,
    });
    setEditDialogOpen(true);
  };

  const handleUpdateKey = () => {
    if (selectedKey) {
      updateKeyMutation.mutate({
        keyId: selectedKey.id,
        data: editFormData,
      });
    }
  };

  const handleRevokeKey = (keyId: number, keyName: string) => {
    if (window.confirm(t('apiKeys.confirmRevokeKey', { name: keyName }))) {
      revokeKeyMutation.mutate(keyId);
    }
  };

  const handleRotateKeys = () => {
    if (window.confirm(t('apiKeys.confirmRotateKeys'))) {
      rotateKeysMutation.mutate();
    }
  };

  const handleCopyKey = (key: string) => {
    navigator.clipboard.writeText(key);
    enqueueSnackbar(t('apiKeys.keyCopiedToClipboard'), { variant: 'success' });
  };

  const getStatusChip = (key: DeviceApiKey) => {
    if (!key.is_active) {
      return <Chip icon={<InactiveIcon />} label={t('apiKeys.status.inactive')} color="error" size="small" />;
    }

    if (key.is_expired) {
      return <Chip icon={<ExpiryIcon />} label={t('apiKeys.status.expired')} color="warning" size="small" />;
    }

    return <Chip icon={<ActiveIcon />} label={t('apiKeys.status.active')} color="success" size="small" />;
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'dd.MM.yyyy HH:mm', { locale: ru });
  };

  if (error) {
    return (
      <Alert severity="error">
        {t('apiKeys.errorLoadingKeys')}: {(error as any).message}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Заголовок и действия */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h6" component="h3">
          <KeyIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          {t('apiKeys.title')} - {deviceName}
        </Typography>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setCreateDialogOpen(true)}
          >
            {t('apiKeys.createKey')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RotateIcon />}
            onClick={handleRotateKeys}
            disabled={rotateKeysMutation.isPending}
          >
            {t('apiKeys.rotateKeys')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => invalidateDeviceApiKeyData(queryClient, deviceId)}
          >
            {t('common.refresh')}
          </Button>
        </Stack>
      </Box>

      {/* Статистика */}
      {statistics && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" gutterBottom>
              {t('apiKeys.statistics')}
            </Typography>
            <Stack direction="row" spacing={4}>
              <Box>
                <Typography variant="h4" color="primary">
                  {statistics.total}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('apiKeys.totalKeys')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="success.main">
                  {statistics.active}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('apiKeys.activeKeys')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="warning.main">
                  {statistics.expired}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('apiKeys.expiredKeys')}
                </Typography>
              </Box>
              <Box>
                <Typography variant="h4" color="info.main">
                  {statistics.recently_used}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('apiKeys.recentlyUsedKeys')}
                </Typography>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Фильтры */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControlLabel
            control={
              <Switch
                checked={includeInactive}
                onChange={(e) => setIncludeInactive(e.target.checked)}
              />
            }
            label={t('apiKeys.showInactiveKeys')}
          />
        </CardContent>
      </Card>

      {/* Таблица ключей */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('apiKeys.keyName')}</TableCell>
              <TableCell>{t('apiKeys.keyPrefix')}</TableCell>
              <TableCell>{t('apiKeys.status')}</TableCell>
              <TableCell>{t('apiKeys.createdAt')}</TableCell>
              <TableCell>{t('apiKeys.lastUsedAt')}</TableCell>
              <TableCell>{t('apiKeys.expiresAt')}</TableCell>
              <TableCell align="center">{t('common.actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <LinearProgress />
                  <Typography align="center" sx={{ mt: 1 }}>
                    {t('common.loading')}...
                  </Typography>
                </TableCell>
              </TableRow>
            ) : apiKeys.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  {t('apiKeys.noKeysFound')}
                </TableCell>
              </TableRow>
            ) : (
              apiKeys.map((key) => (
                <TableRow key={key.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {key.name}
                    </Typography>
                    {key.description && (
                      <Typography variant="caption" color="text.secondary">
                        {key.description}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontFamily="monospace">
                      {key.key_prefix}...
                    </Typography>
                  </TableCell>
                  <TableCell>{getStatusChip(key)}</TableCell>
                  <TableCell>{formatDate(key.created_at)}</TableCell>
                  <TableCell>{formatDate(key.last_used_at)}</TableCell>
                  <TableCell>{formatDate(key.expires_at)}</TableCell>
                  <TableCell align="center">
                    <Stack direction="row" spacing={1} justifyContent="center">
                      <Tooltip title={t('apiKeys.editKey')}>
                        <IconButton
                          size="small"
                          onClick={() => handleEditKey(key)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      {key.is_active && (
                        <Tooltip title={t('apiKeys.revokeKey')}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRevokeKey(key.id, key.name)}
                          >
                            <RevokeIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Диалог создания ключа */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('apiKeys.createNewKey')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('apiKeys.keyName')}
              value={createFormData.name}
              onChange={(e) => setCreateFormData({ ...createFormData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('apiKeys.description')}
              value={createFormData.description}
              onChange={(e) => setCreateFormData({ ...createFormData, description: e.target.value })}
              multiline
              rows={3}
            />
            <TextField
              fullWidth
              type="number"
              label={t('apiKeys.expiresHours')}
              value={createFormData.expires_hours || ''}
              onChange={(e) => setCreateFormData({
                ...createFormData,
                expires_hours: e.target.value ? parseInt(e.target.value) : undefined
              })}
              inputProps={{ min: 1, max: 8760 }}
              helperText={t('apiKeys.expiresHoursHelp')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleCreateKey}
            variant="contained"
            disabled={!createFormData.name || createKeyMutation.isPending}
          >
            {createKeyMutation.isPending ? t('common.creating') : t('common.create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог редактирования ключа */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('apiKeys.editKey')}</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              fullWidth
              label={t('apiKeys.keyName')}
              value={editFormData.name}
              onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label={t('apiKeys.description')}
              value={editFormData.description}
              onChange={(e) => setEditFormData({ ...editFormData, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editFormData.is_active}
                  onChange={(e) => setEditFormData({ ...editFormData, is_active: e.target.checked })}
                />
              }
              label={t('apiKeys.isActive')}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>
            {t('common.cancel')}
          </Button>
          <Button
            onClick={handleUpdateKey}
            variant="contained"
            disabled={!editFormData.name || updateKeyMutation.isPending}
          >
            {updateKeyMutation.isPending ? t('common.updating') : t('common.update')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Диалог просмотра нового ключа */}
      <Dialog open={viewKeyDialogOpen} onClose={() => setViewKeyDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('apiKeys.newKeyCreated')}</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {t('apiKeys.saveKeyWarning')}
          </Alert>
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              {t('apiKeys.apiKey')}
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              <TextField
                fullWidth
                value={newApiKey}
                InputProps={{
                  readOnly: true,
                  style: { fontFamily: 'monospace', fontSize: '0.875rem' }
                }}
                multiline
                rows={3}
              />
              <Tooltip title={t('apiKeys.copyKey')}>
                <IconButton onClick={() => handleCopyKey(newApiKey)}>
                  <CopyIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewKeyDialogOpen(false)} variant="contained">
            {t('common.close')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeviceApiKeysList;
